import {
  isLoadableDisplayPreviewUrl,
  isManagedStorageUrl,
  isSignedViewEnabled,
  isSignedViewPath,
  isUploadsStoragePath,
  toManagedCanonicalUploadsUrl,
} from '@/utils/imageDisplayAccess';
import {
  isDirectDisplayImageUrl,
  isLikelyBrowserReachableThumb,
} from '@/shein-platform/modules/products/components/SheinProductThumb';

/** 发品槽位候选：受管地址优先；提交仍用 sheinUrl。 */
export function collectUploadImageCandidates(item) {
  return [
    item?.ipeUrl,
    item?.publicUrl,
    item?.url,
    item?.previewUrl,
    item?.sheinUrl,
  ].map((u) => String(u || '').trim()).filter(Boolean);
}

function isEphemeralThumbUrl(url) {
  const u = String(url || '').trim().toLowerCase();
  return u.startsWith('blob:') || u.startsWith('data:');
}

/** 任意形态的受管 uploads → 稳定 canonical `/uploads/{key}` */
export function toCanonicalUploadsPath(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  const fromHelper = toManagedCanonicalUploadsUrl(raw);
  if (fromHelper) return fromHelper;
  if (!isUploadsStoragePath(raw) && !isManagedStorageUrl(raw)) return '';
  try {
    const path = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'https://local').pathname;
    const idx = path.indexOf('/uploads/');
    if (idx >= 0) {
      const sliced = path.slice(idx).split('?')[0];
      return toManagedCanonicalUploadsUrl(sliced) || sliced;
    }
  } catch {
    /* ignore */
  }
  if (raw.startsWith('/uploads/')) return raw.split('?')[0];
  return '';
}

function isManagedUploadsCandidate(url) {
  return Boolean(toCanonicalUploadsPath(url) || isUploadsStoragePath(url) || isManagedStorageUrl(url));
}

/**
 * 发品槽位预览：
 * 1) 同页 blob/data → 直出（刚上传，不依赖 sign-batch）
 * 2) 已签名 /view（upload-pic 的 publicUrl）→ 直出，勿再压成 /uploads/ 去换签
 * 3) canonical /uploads/ → SignedImage（刷新后；须 sign-batch 能签出）
 * 4) 测网 shein CDN → 不用
 *
 * 禁止：把 `http://localhost:2006/uploads/…` 喂给裸 img（跨端口/需鉴权必裂图）。
 * 失效 blob 由 sanitize/persist 在落库与回填时剥掉，不在这里抢 durable。
 */
export function resolveUploadThumbSrc(item) {
  const candidates = collectUploadImageCandidates(item);

  for (const raw of candidates) {
    if (isEphemeralThumbUrl(raw)) return raw;
  }

  for (const raw of candidates) {
    if (!isSignedViewPath(raw) && !isLoadableDisplayPreviewUrl(raw)) continue;
    if (isManagedUploadsCandidate(raw) && !isSignedViewPath(raw)) continue;
    if (isSignedViewPath(raw)) return toRelativeSignedView(raw);
    if (isDirectDisplayImageUrl(raw) && isLikelyBrowserReachableThumb(raw)) return raw;
  }

  for (const raw of candidates) {
    const canonical = toCanonicalUploadsPath(raw);
    if (canonical) return canonical;
  }

  return '';
}

/** 绝对 localhost:2006/view → 相对 /api/storage/images/view，走前端代理 */
function toRelativeSignedView(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'https://local');
    if (parsed.pathname.startsWith('/api/storage/images/view')) {
      return `${parsed.pathname}${parsed.search}`;
    }
  } catch {
    /* ignore */
  }
  return raw;
}

/**
 * 受管 uploads 在 signed-view 开启时必须走 SignedImage；
 * blob / 已签名 view / 公网 CDN 才允许裸 img。
 */
export function shouldUseSignedThumb(src) {
  const u = String(src || '').trim();
  if (!u || isEphemeralThumbUrl(u)) return false;
  if (isSignedViewPath(u)) return false;
  if (isManagedUploadsCandidate(u)) {
    return isSignedViewEnabled();
  }
  return false;
}
